import { ArrowRight, Box, Layers, type LucideIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EvidenceLinks } from '@/components/schema/agent/SchemaSources'
import { Badge } from '@/components/ui/badge'
import type { SchemaProposal } from '@/services/api/schemaSessions'
import { cn } from '@/utils/cn'
import { ProposalPropertyLegend, ProposalPropertyList } from './ProposalPropertyList'
import { type ProposalElement, type ProposalSection, proposalElements } from './proposal-preview'
import { AskSchemaAgent } from './SchemaConversationContext'

const SECTIONS: readonly ProposalSection[] = ['entities', 'relations', 'patterns']

const SECTION_ICON: Record<ProposalSection, LucideIcon> = {
  entities: Box,
  relations: ArrowRight,
  patterns: Layers,
}

const SECTION_TONE: Record<ProposalSection, string> = {
  entities: 'text-info-foreground',
  relations: 'text-warning-foreground',
  patterns: 'text-muted-foreground',
}

export function ProposalSchemaPreview({ proposal }: { proposal: SchemaProposal }) {
  const { t } = useTranslation()
  const elements = proposalElements(proposal)
  const [selected, setSelected] = useState<string | null>(null)
  const item = elements.find(element => element.key === selected) ?? elements[0]
  return (
    <div className="@container space-y-4">
      <p className="text-sm text-muted-foreground">
        {t('schemaAgent.workbench.proposalPreviewHint')}
      </p>
      <div className="grid grid-cols-1 gap-4 @[44rem]:grid-cols-[15rem_minmax(0,1fr)]">
        <ProposalTypeNav elements={elements} selectedKey={item?.key} onSelect={setSelected} />
        {item && <ProposalElementDetail item={item} proposal={proposal} />}
      </div>
    </div>
  )
}

function ProposalTypeNav({
  elements,
  selectedKey,
  onSelect,
}: {
  elements: ProposalElement[]
  selectedKey: string | undefined
  onSelect: (key: string) => void
}) {
  const { t } = useTranslation()
  return (
    <nav
      aria-label={t('schemaAgent.workbench.proposalPreview')}
      className="max-h-60 max-md:max-h-[45dvh] space-y-4 overflow-y-auto pr-1 scrollbar-dark @[44rem]:max-h-[600px]"
    >
      {SECTIONS.map(section => {
        const group = elements.filter(element => element.section === section)
        if (group.length === 0) return null
        return (
          <div key={section}>
            <div className="mb-1 flex items-baseline justify-between px-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <span>{t(`schemaAgent.editor.${section}`)}</span>
              <span className="tabular-nums">{group.length}</span>
            </div>
            <ul className="space-y-0.5">
              {group.map(element => (
                <li key={element.key}>
                  <ProposalTypeRow
                    element={element}
                    selected={element.key === selectedKey}
                    onSelect={onSelect}
                  />
                </li>
              ))}
            </ul>
          </div>
        )
      })}
    </nav>
  )
}

function ProposalTypeRow({
  element,
  selected,
  onSelect,
}: {
  element: ProposalElement
  selected: boolean
  onSelect: (key: string) => void
}) {
  const Icon = SECTION_ICON[element.section]
  const showCount = element.section !== 'patterns'
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(element.key)}
      className={cn(
        'flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected
          ? 'border-info-border bg-muted text-foreground'
          : 'border-transparent text-foreground/90 hover:bg-muted/50'
      )}
    >
      <Icon className={cn('h-3.5 w-3.5 shrink-0', SECTION_TONE[element.section])} />
      <span
        className={cn(
          'min-w-0 flex-1 truncate',
          element.section !== 'entities' && 'font-mono text-xs'
        )}
      >
        {element.label}
      </span>
      {showCount && (
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {element.properties.length}
        </span>
      )}
    </button>
  )
}

function ProposalElementDetail({
  item,
  proposal,
}: {
  item: ProposalElement
  proposal: SchemaProposal
}) {
  const { t } = useTranslation()
  const reason = proposal.typeReasons[item.label]
  const Icon = SECTION_ICON[item.section]
  return (
    <article className="min-w-0 space-y-5 rounded-lg border border-border bg-card/50 p-5">
      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Icon className={cn('h-5 w-5 shrink-0', SECTION_TONE[item.section])} />
          <h3
            className={cn(
              'break-words text-lg font-semibold text-foreground',
              item.section !== 'entities' && 'font-mono text-base'
            )}
          >
            {item.label}
          </h3>
          <TypeTraits item={item} />
        </div>
        {item.description && <p className="text-sm text-muted-foreground">{item.description}</p>}
      </header>

      {item.section === 'relations' && <Endpoints item={item} />}

      {item.section !== 'patterns' && (
        <section className="space-y-2">
          <SectionLabel>{t('schemaAgent.editor.properties')}</SectionLabel>
          <ProposalPropertyList properties={item.properties} />
          {item.properties.length > 0 && <ProposalPropertyLegend />}
        </section>
      )}

      {reason && (
        <section className="space-y-2">
          <SectionLabel>{t('schemaAgent.workbench.why')}</SectionLabel>
          <p className="text-sm text-foreground/90">{reason.why}</p>
          <EvidenceLinks evidence={reason.evidence} sources={proposal.sources ?? []} />
        </section>
      )}

      <AskSchemaAgent label={item.label} scope="proposal" />

      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground">
          {t('schemaAgent.workbench.definition')}
        </summary>
        <pre className="mt-3 whitespace-pre-wrap break-words rounded-md bg-muted/30 p-3">
          {JSON.stringify(item.definition, null, 2)}
        </pre>
      </details>
    </article>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  )
}

function TypeTraits({ item }: { item: ProposalElement }) {
  const { t } = useTranslation()
  return (
    <>
      {item.trackable && (
        <Badge variant="outline" className="h-5 text-[11px]">
          {t('schemaAgent.workbench.trait.trackable')}
        </Badge>
      )}
      {item.mergeStrategy && (
        <Badge variant="outline" className="h-5 text-[11px]">
          {t('schemaAgent.workbench.trait.merge', { strategy: item.mergeStrategy })}
        </Badge>
      )}
    </>
  )
}

function Endpoints({ item }: { item: ProposalElement }) {
  const { t } = useTranslation()
  if (item.sourceTypes.length === 0 && item.targetTypes.length === 0) return null
  return (
    <section className="space-y-2">
      <SectionLabel>{t('schemaAgent.workbench.connects')}</SectionLabel>
      <div className="flex flex-wrap items-center gap-1.5 text-sm">
        <TypeChips labels={item.sourceTypes} />
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <TypeChips labels={item.targetTypes} />
      </div>
    </section>
  )
}

function TypeChips({ labels }: { labels: string[] }) {
  if (labels.length === 0) return <span className="text-muted-foreground">*</span>
  return (
    <>
      {labels.map(label => (
        <Badge key={label} variant="secondary" className="h-5 text-xs font-normal">
          {label}
        </Badge>
      ))}
    </>
  )
}
